import { defineStore } from 'pinia';
import { usePostMessage } from '@/composables/usePostMessage';
import { useStageStore } from '@/stores/stage';
import type Layer from '@/types/Layer';
import {
    normalizeZonePrices,
    resolveZoneCombination,
    type ZonePrice,
    type ZonePriceResolution
} from '@/utils/zonePricing';

/** Clé de `patternLayersByZone` pour un fond appliqué à tout le produit (pas une zone). */
const GLOBAL_PATTERN_KEY = '__global__';

/**
 * PRIX PAR ZONE (mode e-commerce uniquement). L'hôte envoie ses combinaisons de
 * zones tarifées (`askZonePrices` → `sendZonePrices`) ; le store en déduit, à
 * partir des calques réellement posés, la combinaison à facturer et son
 * supplément unitaire, que `productStore.prices` ajoute au prix affiché.
 *
 * Tant que l'hôte n'a rien envoyé (ou en standalone), la liste est vide : aucun
 * supplément, comportement historique inchangé.
 */
export const useZonePricingStore = defineStore('zonePricing', {
    state: () => ({
        enabled: typeof window !== 'undefined' && window.parent !== window,
        zonePrices: [] as ZonePrice[],
        requested: false,
        /** Liste repliée par l'utilisateur pour dégager le canvas (partagé entre panneau et feuille de validation). */
        collapsed: false
    }),
    getters: {
        hasZonePrices: (state): boolean => state.zonePrices.length > 0,

        /**
         * Zones portant du contenu imprimé : calques libres, calques de thème et
         * fonds par zone. Les calques globaux (zoneId null) ne désignent aucune
         * zone et ne comptent pas.
         */
        usedZoneIds(): string[] {
            const stageStore = useStageStore();
            const zoneIds = new Set<string>();
            const collect = (layer: Layer | undefined) => {
                if (layer?.zoneId) {
                    zoneIds.add(String(layer.zoneId));
                }
            };

            stageStore.layers.forEach(collect);
            stageStore.themeAggregates.forEach((aggregate) => aggregate.children.forEach(collect));
            Object.entries(stageStore.patternLayersByZone).forEach(([zoneId, layer]) => {
                if (zoneId !== GLOBAL_PATTERN_KEY && layer) {
                    zoneIds.add(zoneId);
                }
            });

            return [...zoneIds];
        },

        resolution(): ZonePriceResolution {
            return resolveZoneCombination(this.zonePrices, this.usedZoneIds);
        },

        /**
         * Entrée facturée quand une seule couvre le design (null = aucun supplément,
         * ou design facturé en plusieurs entrées : voir `activePieceIds`).
         */
        activeCombination(): ZonePrice | null {
            return this.resolution.combination;
        },

        /** Ids des entrées facturées pour le design courant, pour les mettre en avant. */
        activePieceIds(): string[] {
            return this.resolution.pieces.map((piece) => piece.id);
        },

        /** Supplément unitaire numérique ajouté au prix de la variante. */
        unitSurcharge(): number {
            return this.resolution.surcharge;
        }
    },
    actions: {
        /** Demande une fois les prix par zone à l'hôte (il peut aussi les pousser d'office). */
        requestZonePrices() {
            if (!this.enabled || this.requested) {
                return;
            }
            this.requested = true;
            usePostMessage().askZonePrices();
        },

        /** Applique une réponse `sendZonePrices` de l'hôte (payload non fiable, validé). */
        applyHostZonePrices(raw: unknown) {
            this.zonePrices = normalizeZonePrices(raw);
        },

        toggleCollapsed() {
            this.collapsed = !this.collapsed;
        }
    }
});
